import axios from 'axios'
import { env } from '../utils/env'
import { CognitoAuthService } from './cognitoAuth'

export const api = axios.create({
  baseURL: `${env.API_URL}/${env.API_VERSION}`,
  timeout: 60000, // 60 seconds for file uploads
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor for auth token
api.interceptors.request.use(
  async (config) => {
    // Validate token and refresh if expired
    const validToken = await CognitoAuthService.validateAndRefreshToken()

    if (validToken) {
      config.headers.Authorization = `Bearer ${validToken}`
    } else {
      // Token validation/refresh failed - check if we have any stored tokens
      const tokens = CognitoAuthService.getStoredTokens()
      if (tokens) {
        // We had tokens but they're invalid and couldn't be refreshed
        console.log('Token validation failed, clearing auth state...')
        await CognitoAuthService.signOut()
        window.location.href = '/login'
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config

    // Handle both 401 (Unauthorized) and 403 (Forbidden) errors
    // 403 can also indicate an expired or invalid token
    const isAuthError = error.response?.status === 401 || error.response?.status === 403

    if (isAuthError && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        // Try to refresh the token
        const newTokens = await CognitoAuthService.refreshSession()
        if (newTokens?.accessToken) {
          // Retry the original request with the new token
          originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`
          return api(originalRequest)
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError)
      }

      // If refresh fails or no tokens, redirect to login
      console.log('Authentication failed, logging out...')
      await CognitoAuthService.signOut()
      window.location.href = '/login'
    }

    return Promise.reject(error)
  }
)

// API Key Roles types
export interface ApiKeyRole {
  id: number;
  name: string;
  description: string;
  is_system_role: boolean;
}

export interface ApiKeyRolesResponse {
  data: ApiKeyRole[];
  metadata: {
    correlation_id: string;
    timestamp: string;
  };
}

// API Key Creation types
export interface CreateApiKeyRequest {
  name: string;
  description: string;
  role_name: string;
  owner_email: string;
  expires_in_days: number;
  daily_quota: number;
  created_by?: string;
  key_metadata: {
    organization: string;
    environment: string;
    contact_person?: string;
  };
}

export interface ApiKeyData {
  id: number;
  key_id: string;
  name: string;
  role_name: string;
  status: string;
  expires_at: string;
  daily_quota: number;
}

export interface CreateApiKeyResponse {
  success: boolean;
  message: string;
  data: {
    api_key: ApiKeyData;
    full_key: string;
    security_notice: string;
  };
  metadata: {
    correlation_id: string;
    timestamp: string;
  };
}

// API Key Listing types
export interface ListApiKeysFilter {
  status?: string;
  role_name?: string;
  owner_email?: string;
  created_by?: string;
}

export interface SortOption {
  field: string;
  direction: 'asc' | 'desc';
}

export interface PaginationRequest {
  page: number;
  page_size: number;
}

export interface ListApiKeysRequest {
  filters?: ListApiKeysFilter;
  sort?: SortOption[];
  pagination: PaginationRequest;
}

export interface ApiKeyListItem {
  id: number;
  key_id: string;
  name: string;
  description: string;
  role_name: string;
  owner_email: string;
  status: string;
  created_by: string;
  approved_by: string;
  approved_at: string;
  last_used_at: string | null;
  expires_at: string;
  daily_quota: number;
  key_metadata: {
    organization?: string;
    environment?: string;
    contact_person?: string;
  };
  created_at: string;
  updated_at: string;
}

export interface PaginationResponse {
  page: number;
  page_size: number;
  total_records: number;
  total_pages: number;
}

export interface ListApiKeysResponse {
  data: ApiKeyListItem[];
  pagination: PaginationResponse;
  metadata: {
    correlation_id: string;
    timestamp: string;
  };
}

// Dashboard Metrics types
export interface DashboardMetrics {
  governed_requests_today: number;
  active_consumers: number;
  successful_requests: number;
  restricted_failed: number;
  average_trust_score: number;
  avg_response_time_ms: number;
}

export interface DashboardMetricsResponse {
  data: DashboardMetrics;
  metadata: {
    correlation_id: string;
    timestamp: string;
  };
}

// Recent Requests types
export interface RecentRequest {
  id: number;
  time: string;
  consumer: string;
  role: string;
  data_requested: string;
  records_returned: number;
  trust_score: number;
  trust_level: string;
  status: string;
  response_time: string;
  correlation_id: string;
  query_filters: string;
  query_complexity: number;
  caller_type: string;
  ip_address: string;
  user_agent: string;
  endpoint: string;
  error_message?: string;
  accessed_at: string;
}

// Trust Quality types
export interface TrustLevelDistribution {
  total_responses: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  high_percentage: number;
  medium_percentage: number;
  low_percentage: number;
}

export interface DataTypeBreakdownItem {
  data_type: string;
  avg_trust_score: number;
  trust_level: string;
  completeness_pct: number;
  validation_pass_rate: number;
  data_freshness: string;
}

export interface TrustQualityMetrics {
  average_trust_score: number;
  average_completeness_pct: number;
  validation_pass_rate: number;
  trust_level_distribution: TrustLevelDistribution;
  data_type_breakdown: DataTypeBreakdownItem[];
}

export interface TrustQualityResponse {
  data: TrustQualityMetrics;
  metadata: {
    correlation_id: string;
    timestamp: string;
  };
}

export interface RecentRequestsFilter {
  role?: string;
  trust_level?: string;
  status?: string;
  start_date?: string;
  end_date?: string;
}

export interface RecentRequestsRequest {
  filters?: RecentRequestsFilter;
  page?: number;
  page_size?: number;
}

export interface RecentRequestsResponse {
  data: RecentRequest[];
  pagination: PaginationResponse;
  total_requests: number;
  metadata: {
    correlation_id: string;
    timestamp: string;
  };
}

// Request Outcomes types
export interface RequestOutcomesSummary {
  restricted_count: number;
  restricted_percentage: number;
  rate_limited_count: number;
  rate_limited_percentage: number;
  error_count: number;
  error_percentage: number;
  total_problematic_requests: number;
  total_requests_analyzed: number;
}

export interface FailedRequest {
  time: string;
  consumer_code: string;
  consumer_name: string;
  role: string;
  data_requested: string;
  status: string;
  error_detail: string;
}

export interface RequestOutcomesRequest {
  page?: number;
  page_size?: number;
  status_filter?: string; // 'Restricted' | 'Rate Limited' | 'Error'
  start_date?: string;
  end_date?: string;
}

export interface RequestOutcomesResponse {
  summary: RequestOutcomesSummary;
  failed_requests: FailedRequest[];
  pagination: PaginationResponse;
  metadata: {
    correlation_id: string;
    timestamp: string;
  };
}

// API Service functions
export const apiService = {
  // Fetch available API key roles for consumer registration
  async getApiKeyRoles(): Promise<ApiKeyRole[]> {
    try {
      const response = await api.get<ApiKeyRolesResponse>('/admin/api-keys/api-key-roles');
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch API key roles:', error);
      throw new Error('Unable to fetch API key roles. Please try again.');
    }
  },

  // Create a new API key
  async createApiKey(request: CreateApiKeyRequest): Promise<CreateApiKeyResponse> {
    try {
      const response = await api.post<CreateApiKeyResponse>('/admin/api-keys/', request);
      return response.data;
    } catch (error: any) {
      console.error('Failed to create API key:', error);

      // Handle specific error responses
      if (error.response?.status === 400) {
        const errorMessage = error.response.data?.message || 'Invalid request data';
        throw new Error(errorMessage);
      } else if (error.response?.status === 401) {
        throw new Error('Unauthorized. Please check your permissions.');
      } else if (error.response?.status === 403) {
        throw new Error('Forbidden. You do not have permission to create API keys.');
      }

      throw new Error('Failed to create API key. Please try again.');
    }
  },

  // List API keys with filtering and pagination
  async listApiKeys(request: ListApiKeysRequest): Promise<ListApiKeysResponse> {
    try {
      const response = await api.post<ListApiKeysResponse>('/admin/api-keys/list', request);
      return response.data;
    } catch (error: any) {
      console.error('Failed to list API keys:', error);

      // Handle specific error responses
      if (error.response?.status === 400) {
        const errorMessage = error.response.data?.message || 'Invalid request parameters';
        throw new Error(errorMessage);
      } else if (error.response?.status === 401) {
        throw new Error('Unauthorized. Please check your permissions.');
      } else if (error.response?.status === 403) {
        throw new Error('Forbidden. You do not have permission to list API keys.');
      }

      throw new Error('Failed to load API keys. Please try again.');
    }
  },

  // Fetch dashboard metrics for governance console
  async getDashboardMetrics(startDate?: string, endDate?: string): Promise<DashboardMetrics> {
    try {
      const params = new URLSearchParams();

      if (startDate) {
        params.append('start_date', startDate);
      }
      if (endDate) {
        params.append('end_date', endDate);
      }

      const queryString = params.toString();
      const url = `/admin/governance/dashboard/metrics${queryString ? `?${queryString}` : ''}`;

      const response = await api.get<DashboardMetricsResponse>(url);
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch dashboard metrics:', error);

      // Handle specific error responses
      if (error.response?.status === 400) {
        const errorMessage = error.response.data?.message || 'Invalid request parameters';
        throw new Error(errorMessage);
      } else if (error.response?.status === 401) {
        throw new Error('Unauthorized. Please check your permissions.');
      } else if (error.response?.status === 403) {
        throw new Error('Forbidden. You do not have permission to view dashboard metrics.');
      }

      throw new Error('Failed to load dashboard metrics. Please try again.');
    }
  },

  // Fetch recent governance requests with filtering and pagination
  async getRecentRequests(request: RecentRequestsRequest): Promise<RecentRequestsResponse> {
    try {
      const params = new URLSearchParams();

      // Add filters to query params
      if (request.filters?.role) {
        params.append('role', request.filters.role);
      }
      if (request.filters?.trust_level) {
        params.append('trust_level', request.filters.trust_level);
      }
      if (request.filters?.status) {
        params.append('status', request.filters.status);
      }
      if (request.filters?.start_date) {
        params.append('start_date', request.filters.start_date);
      }
      if (request.filters?.end_date) {
        params.append('end_date', request.filters.end_date);
      }

      // Add pagination params
      if (request.page) {
        params.append('page', request.page.toString());
      }
      if (request.page_size) {
        params.append('page_size', request.page_size.toString());
      }

      const queryString = params.toString();
      const url = `/admin/governance/dashboard/recent-requests${queryString ? `?${queryString}` : ''}`;

      const response = await api.get<RecentRequestsResponse>(url);
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch recent requests:', error);

      // Handle specific error responses
      if (error.response?.status === 400) {
        const errorMessage = error.response.data?.message || 'Invalid request parameters';
        throw new Error(errorMessage);
      } else if (error.response?.status === 401) {
        throw new Error('Unauthorized. Please check your permissions.');
      } else if (error.response?.status === 403) {
        throw new Error('Forbidden. You do not have permission to view recent requests.');
      }

      throw new Error('Failed to load recent requests. Please try again.');
    }
  },

  // Fetch trust quality metrics for governance console
  async getTrustQualityMetrics(startDate?: string, endDate?: string): Promise<TrustQualityMetrics> {
    try {
      const params = new URLSearchParams();

      if (startDate) {
        params.append('start_date', startDate);
      }
      if (endDate) {
        params.append('end_date', endDate);
      }

      const queryString = params.toString();
      const url = `/admin/governance/dashboard/trust-quality${queryString ? `?${queryString}` : ''}`;

      const response = await api.get<TrustQualityResponse>(url);
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch trust quality metrics:', error);

      // Handle specific error responses
      if (error.response?.status === 400) {
        const errorMessage = error.response.data?.message || 'Invalid request parameters';
        throw new Error(errorMessage);
      } else if (error.response?.status === 401) {
        throw new Error('Unauthorized. Please check your permissions.');
      } else if (error.response?.status === 403) {
        throw new Error('Forbidden. You do not have permission to view trust quality metrics.');
      }

      throw new Error('Failed to load trust quality metrics. Please try again.');
    }
  },

  // Fetch request outcomes (restricted, rate limited, failed requests) for governance console
  async getRequestOutcomes(request: RequestOutcomesRequest): Promise<RequestOutcomesResponse> {
    try {
      const params = new URLSearchParams();

      // Add pagination params
      if (request.page) {
        params.append('page', request.page.toString());
      }
      if (request.page_size) {
        params.append('page_size', request.page_size.toString());
      }
      // Add status filter if provided
      if (request.status_filter) {
        params.append('status_filter', request.status_filter);
      }
      // Add date filters if provided
      if (request.start_date) {
        params.append('start_date', request.start_date);
      }
      if (request.end_date) {
        params.append('end_date', request.end_date);
      }

      const queryString = params.toString();
      const url = `/admin/governance/dashboard/request-outcomes${queryString ? `?${queryString}` : ''}`;

      const response = await api.get<RequestOutcomesResponse>(url);
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch request outcomes:', error);

      // Handle specific error responses
      if (error.response?.status === 400) {
        const errorMessage = error.response.data?.message || 'Invalid request parameters';
        throw new Error(errorMessage);
      } else if (error.response?.status === 401) {
        throw new Error('Unauthorized. Please check your permissions.');
      } else if (error.response?.status === 403) {
        throw new Error('Forbidden. You do not have permission to view request outcomes.');
      }

      throw new Error('Failed to load request outcomes. Please try again.');
    }
  },
};

export default api